const DEFAULT_SECRET = (process.env.REACT_APP_SHARE_SECRET || 'monu-design-share-secret').toString();

const textEncoder = new TextEncoder();
const textDecoder = new TextDecoder();

const toBase64Url = (bytes: Uint8Array): string => {
  let binary = '';
  for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
  const b64 = btoa(binary);
  return b64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
};

const fromBase64Url = (s: string): Uint8Array => {
  const b64 = s.replace(/-/g, '+').replace(/_/g, '/');
  const pad = b64.length % 4 === 0 ? '' : '='.repeat(4 - (b64.length % 4));
  const binary = atob(b64 + pad);
  const out = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) out[i] = binary.charCodeAt(i);
  return out;
};

const concatBytes = (...parts: Uint8Array[]): Uint8Array => {
  const total = parts.reduce((sum, p) => sum + p.length, 0);
  const out = new Uint8Array(total);
  let offset = 0;
  for (const p of parts) {
    out.set(p, offset);
    offset += p.length;
  }
  return out;
};

const toArrayBuffer = (u8: Uint8Array): ArrayBuffer => {
  const ab = new ArrayBuffer(u8.byteLength);
  new Uint8Array(ab).set(u8);
  return ab;
};

const getSubtle = (): SubtleCrypto => {
  const c: any = (window as any).crypto || (window as any).msCrypto;
  const subtle: any = c && c.subtle;
  if (!subtle) {
    throw new Error('Sharing requires a secure context (HTTPS or localhost).');
  }
  return subtle as SubtleCrypto;
};

const getCryptoProvider = (): Crypto => {
  const c: any = (window as any).crypto || (window as any).msCrypto;
  if (!c) {
    throw new Error('Sharing requires a secure context (HTTPS or localhost).');
  }
  return c as Crypto;
};

export const isClientShareCryptoAvailable = (): boolean => {
  try {
    const c: any = (window as any).crypto || (window as any).msCrypto;
    return Boolean(c && c.subtle && typeof c.getRandomValues === 'function');
  } catch {
    return false;
  }
};

const deriveKey = async (secret: string): Promise<CryptoKey> => {
  const subtle = getSubtle();
  const secretBytes = textEncoder.encode(secret);
  const baseKey = await subtle.importKey('raw', secretBytes, 'PBKDF2', false, ['deriveKey']);
  const salt = textEncoder.encode('monu-design-share-v1');
  return subtle.deriveKey(
    { name: 'PBKDF2', salt, iterations: 120_000, hash: 'SHA-256' },
    baseKey,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt']
  );
};

const gzipIfPossible = async (bytes: Uint8Array): Promise<{ bytes: Uint8Array; compressed: boolean }> => {
  try {
    const CS: any = (window as any).CompressionStream;
    if (!CS) return { bytes, compressed: false };
    const cs = new CS('gzip');
    const w = cs.writable.getWriter();
    await w.write(toArrayBuffer(bytes) as any);
    await w.close();
    const ab = await new Response(cs.readable).arrayBuffer();
    return { bytes: new Uint8Array(ab), compressed: true };
  } catch {
    return { bytes, compressed: false };
  }
};

const gunzipIfPossible = async (bytes: Uint8Array): Promise<Uint8Array> => {
  const DS: any = (window as any).DecompressionStream;
  if (!DS) throw new Error('DecompressionStream not available');
  const ds = new DS('gzip');
  const w = ds.writable.getWriter();
  await w.write(toArrayBuffer(bytes) as any);
  await w.close();
  const ab = await new Response(ds.readable).arrayBuffer();
  return new Uint8Array(ab);
};

export async function encryptSharedDesignToUrlParam(payload: any, opts?: { secret?: string }): Promise<string> {
  const secret = (opts?.secret || DEFAULT_SECRET).toString();
  const cryptoProvider = getCryptoProvider();
  const subtle = getSubtle();
  const key = await deriveKey(secret);

  const json = JSON.stringify(payload ?? null);
  const plainBytes = textEncoder.encode(json);
  const { bytes: dataBytes, compressed } = await gzipIfPossible(plainBytes);

  if (typeof (cryptoProvider as any).getRandomValues !== 'function') {
    throw new Error('Sharing requires a secure context (HTTPS or localhost).');
  }
  const iv = (cryptoProvider as any).getRandomValues(new Uint8Array(12));
  const cipherBuf = await subtle.encrypt({ name: 'AES-GCM', iv }, key, toArrayBuffer(dataBytes));
  const cipherBytes = new Uint8Array(cipherBuf);

  const version = compressed ? 2 : 1;
  const packed = concatBytes(new Uint8Array([version]), iv, cipherBytes);
  return toBase64Url(packed);
}

export async function decryptSharedDesignFromUrlParam(token: string, opts?: { secret?: string }): Promise<any> {
  const secret = (opts?.secret || DEFAULT_SECRET).toString();
  const subtle = getSubtle();
  const key = await deriveKey(secret);

  const packed = fromBase64Url(String(token || ''));
  if (packed.length < 1 + 12 + 1) throw new Error('Invalid token');

  const version = packed[0];
  const iv = new Uint8Array(Array.from(packed.slice(1, 13)));
  const cipher = new Uint8Array(Array.from(packed.slice(13)));

  const plainBuf = await subtle.decrypt({ name: 'AES-GCM', iv }, key, toArrayBuffer(cipher));
  let plainBytes: Uint8Array = new Uint8Array(plainBuf);

  if (version === 2) {
    plainBytes = await gunzipIfPossible(plainBytes);
  } else if (version !== 1) {
    throw new Error('Unsupported token version');
  }

  const json = textDecoder.decode(plainBytes);
  return JSON.parse(json);
}
